import React from "react";
import { useNavigate } from "react-router-dom";
import { Activity, asyncSetTimeout, MeetingActivityTypes, MeetingInvitationStatus } from "../../../amorg-common";
import { deleteActivity, getMeetingInvitationStatus, sendMeetingInvitation } from "../../../api/activityClientApi";
import { PageHeader } from "../../../designSystem/PageHeader";
import { ToolBarButton, ToolBarContainer } from "../../../designSystem/compositeComponents/ButtonToolBar";
import { DeleteConfirmationModal } from "../../../designSystem/compositeComponents/DeleteConfirmationModal";
import { txt } from "../../../txt";
import { ActivityInvitationStatusPanel } from "./ActivityInvitationStatusPanel";
import { SingleColumnLayout } from "../../_layouts/SingleColumnLayout";
import { PrintButtonWithContent } from "../PrintButtonWithContent";

type ActivityHeaderProps =  {
    activity: Activity,
    deleteButton:{
        header:string,
        body:string
    } | undefined,
    printButton?:{
        documentTitle:string,
        printComponent: React.ReactNode
    },
    onActivityUpdated?: () => void,
    isLoading?: (value:boolean) => void,
    additionalToolBarButtons?:React.ReactNode[],
    hideDeleteButton?: boolean,
    messageCardHeader?:React.ReactNode
}

export function ActivityHeader(props:ActivityHeaderProps){

    const [inDelete, setInDelete] = React.useState(false);

    const navigate = useNavigate();

    async function handleClickDeleteActivity() 
    {
        await deleteActivity(props.activity.id);
        setInDelete(false);
        navigate(-1);
    }

    const printHandler = () => {
        return props.printButton ? props.printButton.printComponent : <></>;
    };
    
    return (
        <>

        {props.messageCardHeader &&
            <SingleColumnLayout>
                {props.messageCardHeader}
            </SingleColumnLayout>
        }
        
        <PageHeader
            headerText={txt.getDynamic(`ActivityType_${props.activity.activityType}`)}
            buttons={
                <ToolBarContainer>
                    {props.additionalToolBarButtons && props.additionalToolBarButtons.map((button, index) => {
                        return <React.Fragment key={index}>{button}</React.Fragment>
                    })}
                    
                    {props.printButton &&
                        <PrintButtonWithContent documentTitle={props.printButton.documentTitle}>
                            {() => printHandler()}
                        </PrintButtonWithContent>
                    }
                    
                    
                    {props.deleteButton &&
                        <>
                            <ToolBarButton
                                icon='interface_actions_delete'
                                tooltip= 'Slet'
                                onClick={ () => setInDelete(true)}
                                variant= 'destructive-text' />

                            <DeleteConfirmationModal
                                showModal={inDelete}
                                confirmationHeading={props.deleteButton.header}
                                confirmationText={props.deleteButton.body}
                                buttonText="Slet"
                                requireKey={true}
                                onConfirmDelete={handleClickDeleteActivity} 
                                onCancel={() => setInDelete(false)} />
                        </>
                    }
                </ToolBarContainer>
            } />
        </>
    );
}


export function MeetingActivityHeader(props:ActivityHeaderProps)
{
    const [invitationStatus, setInvitationStatus] = React.useState<MeetingInvitationStatus | null>(null);

    React.useEffect(() => {
        async function getInvitationStatus() {
            setInvitationStatus(await getMeetingInvitationStatus(props.activity.id));
            props.isLoading?.(false);
        }
        if(showInvitationButtons)
        {
            getInvitationStatus();
        }
    }, [props.activity]);

    
    async function handleClickSendInvitation()
    {
        if(showInvitationButtons)
        {
            props.isLoading?.(true);
            await asyncSetTimeout(async () => { return; }, 1000);
            sendMeetingInvitation(props.activity.id);
            props.onActivityUpdated?.();
            props.isLoading?.(false);
        }
    }
    
    const showInvitationButtons = MeetingActivityTypes.includes(props.activity.activityType);

    return (
        <ActivityHeader 
            {...props}
            messageCardHeader={
                <>
                {invitationStatus &&
                    <ActivityInvitationStatusPanel 
                        status={invitationStatus.status}
                        activity={{start: props.activity.start, participantCount: props.activity.participants.length}}
                        onClick={handleClickSendInvitation} />
                }
                </>
        } />
    );
}